// PART 5: self-contained QR Code encoder -- NO npm dependency (this app
// must build offline/CSP-safe). Fixed to a SINGLE configuration: Version 4
// (33x33 modules), Error Correction Level M, byte mode -- comfortably fits
// a ~40-60 char share URL (empirically + by the ISO/IEC 18004 capacity
// formula, the byte-mode capacity at this version/EC level is exactly 62
// characters, see QR_MAX_BYTES below) and needs only a SINGLE Reed-Solomon
// block pair (2 EQUAL-size blocks of 32 data codewords each -- see
// RS_BLOCK_TABLE in the ISO spec / any reference implementation), which
// keeps the interleaving step a plain alternation instead of the general
// variable-block-size case.
//
// Every structural constant and algorithm step below (bit-buffer packing,
// GF(256) log/antilog tables, Reed-Solomon generator-polynomial
// construction via polynomial division, the zigzag data-placement walk,
// the 8 mask formulas, the 4-rule mask-penalty scoring, and the format-info
// BCH(15,5) encoding) is a direct, faithful port of the ISO/IEC 18004
// algorithm as implemented by the well-established Python `qrcode` package
// (github.com/lincolnloop/python-qrcode) -- ported line-for-line, not
// re-derived from memory of the spec, specifically so it can be checked
// against that same reference. tests/unit/qr.test.ts encodes a fixed,
// realistic share-link string and asserts the result is BYTE-IDENTICAL to
// the golden 33x33 matrix that reference library produces for the exact
// same input/version/EC-level (generated once, offline, via
// `python -m pip install qrcode` and dumping `qr.modules` -- see that
// test's own comment for the exact repro command) -- i.e. this is checked
// against an independent, trusted implementation, not just self-consistent
// with its own decoder.

const VERSION = 4;
export const QR_SIZE = VERSION * 4 + 17; // 33

const NUM_BLOCKS = 2;
const DATA_CODEWORDS_PER_BLOCK = 32;
const EC_CODEWORDS_PER_BLOCK = 18;
const TOTAL_DATA_CODEWORDS = NUM_BLOCKS * DATA_CODEWORDS_PER_BLOCK; // 64
const BIT_LIMIT = TOTAL_DATA_CODEWORDS * 8; // 512

/** Byte-mode capacity in characters at this fixed version/EC level: mode
 * indicator (4 bits) + character-count indicator (8 bits, versions 1-9) +
 * 8 bits/char must fit within BIT_LIMIT (512) -- (512-12)/8 = 62.5, floored
 * to 62. Matches the ISO/IEC 18004 Annex-listed capacity table exactly. */
export const QR_MAX_BYTES = Math.floor((BIT_LIMIT - 12) / 8);

const PAD0 = 0xec;
const PAD1 = 0x11;

// ---- GF(256) log/antilog tables --------------------------------------
// Primitive polynomial x^8 + x^4 + x^3 + x^2 + 1 (0x11D), generator 2 --
// the exact QR-mandated field. This recurrence (rather than repeated
// multiply-by-2-and-reduce) is the identical construction the reference
// library uses; ported verbatim so the resulting tables are bit-identical.
const EXP_TABLE = new Uint8Array(256);
const LOG_TABLE = new Uint8Array(256);
for (let i = 0; i < 8; i++) EXP_TABLE[i] = 1 << i;
for (let i = 8; i < 256; i++) {
  EXP_TABLE[i] = EXP_TABLE[i - 4]! ^ EXP_TABLE[i - 5]! ^ EXP_TABLE[i - 6]! ^ EXP_TABLE[i - 8]!;
}
for (let i = 0; i < 255; i++) {
  LOG_TABLE[EXP_TABLE[i]!] = i;
}

function glog(n: number): number {
  if (n < 1) throw new Error(`glog(${n})`);
  return LOG_TABLE[n]!;
}
function gexp(n: number): number {
  // Python's `%` always returns a non-negative result for a positive
  // modulus even when `n` is negative -- replicate that (JS `%` does not).
  const m = ((n % 255) + 255) % 255;
  return EXP_TABLE[m]!;
}

/** GF(256) polynomial, coefficients highest-degree-first (same convention
 * as the reference `Polynomial` class). `shift` appends that many
 * zero-coefficient low-order terms (i.e. multiplies by x^shift) -- used to
 * build the "message shifted left by ecCount" dividend for systematic RS
 * encoding. */
function polyMul(a: readonly number[], b: readonly number[]): number[] {
  const out = new Array<number>(a.length + b.length - 1).fill(0);
  for (let i = 0; i < a.length; i++) {
    if (a[i] === 0) continue;
    for (let j = 0; j < b.length; j++) {
      if (b[j] === 0) continue;
      out[i + j]! ^= gexp(glog(a[i]!) + glog(b[j]!));
    }
  }
  return out;
}

/** Polynomial long division remainder in GF(256): `a mod b`, both
 * highest-degree-first. Iterative (not the reference's recursion) but the
 * exact same per-step math. */
function polyMod(a: readonly number[], b: readonly number[]): number[] {
  let num = a.slice();
  // Strip leading zero coefficients (mirrors the reference Polynomial
  // constructor's own `offset` trim), otherwise the degree comparison below
  // is wrong for a dividend that starts with zero terms.
  let off = 0;
  while (off < num.length - 1 && num[off] === 0) off++;
  num = num.slice(off);
  while (num.length >= b.length) {
    if (num[0] === 0) {
      num = num.slice(1);
      continue;
    }
    const ratio = glog(num[0]!) - glog(b[0]!);
    for (let i = 0; i < b.length; i++) {
      num[i]! ^= gexp(glog(b[i]!) + ratio);
    }
    // Drop the (now-zero) leading term.
    let k = 0;
    while (k < num.length - 1 && num[k] === 0) k++;
    num = num.slice(k);
  }
  return num;
}

/** Builds the Reed-Solomon generator polynomial of the given degree:
 * (x - a^0)(x - a^1)...(x - a^(degree-1)) over GF(256), i.e. the standard
 * QR generator-polynomial construction. */
function rsGeneratorPoly(degree: number): number[] {
  let g = [1];
  for (let i = 0; i < degree; i++) {
    g = polyMul(g, [1, gexp(i)]);
  }
  return g;
}

/** Systematic Reed-Solomon encode: `dataCodewords` (length D) -> `ecCount`
 * parity codewords, via `(dataCodewords shifted left by ecCount) mod
 * generator`. */
function rsEncodeBlock(dataCodewords: readonly number[], ecCount: number): number[] {
  const generator = rsGeneratorPoly(ecCount);
  const shifted = [...dataCodewords, ...new Array<number>(ecCount).fill(0)];
  const remainder = polyMod(shifted, generator);
  // The remainder may be shorter than `ecCount` (high-order zero
  // coefficients already stripped by polyMod) -- left-pad with zeros to the
  // full EC length, same as the reference's `modIndex >= 0 ? ... : 0`.
  const ec = new Array<number>(ecCount).fill(0);
  const offset = ecCount - remainder.length;
  for (let i = 0; i < remainder.length; i++) ec[offset + i] = remainder[i]!;
  return ec;
}

// ---- bit buffer (MSB-first, matches the reference BitBuffer) ----------
class BitBuffer {
  bytes: number[] = [];
  length = 0; // bits written

  putBit(bit: boolean): void {
    const byteIndex = this.length >> 3;
    if (this.bytes.length <= byteIndex) this.bytes.push(0);
    if (bit) this.bytes[byteIndex]! |= 0x80 >> this.length % 8;
    this.length++;
  }

  put(value: number, bitCount: number): void {
    for (let i = 0; i < bitCount; i++) {
      this.putBit(((value >> (bitCount - i - 1)) & 1) === 1);
    }
  }
}

/** Builds the 64 interleaved... no -- the 64 pre-interleave DATA codewords
 * for byte-mode content `bytes` (mode indicator, 8-bit character count,
 * raw bytes, up-to-4-bit terminator, byte-align padding, then alternating
 * 0xEC/0x11 fill codewords), exactly mirroring `create_data`. Returns
 * `null` if `bytes` exceeds `QR_MAX_BYTES` (the same "would overflow
 * bit_limit" check the reference raises `DataOverflowError` for). */
function buildDataCodewords(bytes: Uint8Array): number[] | null {
  const buf = new BitBuffer();
  buf.put(0b0100, 4); // byte-mode indicator
  buf.put(bytes.length, 8); // character-count indicator (versions 1-9, byte mode)
  for (const b of bytes) buf.put(b, 8);

  if (buf.length > BIT_LIMIT) return null;

  for (let i = 0; i < Math.min(BIT_LIMIT - buf.length, 4); i++) buf.putBit(false);

  const delimit = buf.length % 8;
  if (delimit) for (let i = 0; i < 8 - delimit; i++) buf.putBit(false);

  const bytesToFill = (BIT_LIMIT - buf.length) / 8;
  for (let i = 0; i < bytesToFill; i++) buf.put(i % 2 === 0 ? PAD0 : PAD1, 8);

  return buf.bytes;
}

/** Splits the 64 data codewords into the 2 equal 32-codeword blocks, RS-
 * encodes each (18 EC codewords/block), then interleaves data-then-EC
 * exactly as `create_bytes` does (equal block sizes here collapse its
 * general "for i in range(maxCount): for each block: ..." loop into a
 * plain alternation). Returns the full 100-codeword sequence. */
function buildInterleavedCodewords(dataCodewords: readonly number[]): number[] {
  const blocks: number[][] = [];
  const ecs: number[][] = [];
  for (let b = 0; b < NUM_BLOCKS; b++) {
    const block = dataCodewords.slice(b * DATA_CODEWORDS_PER_BLOCK, (b + 1) * DATA_CODEWORDS_PER_BLOCK);
    blocks.push(block);
    ecs.push(rsEncodeBlock(block, EC_CODEWORDS_PER_BLOCK));
  }
  const out: number[] = [];
  for (let i = 0; i < DATA_CODEWORDS_PER_BLOCK; i++) for (const block of blocks) out.push(block[i]!);
  for (let i = 0; i < EC_CODEWORDS_PER_BLOCK; i++) for (const ec of ecs) out.push(ec[i]!);
  return out;
}

// ---- module matrix construction ----------------------------------------

type Cell = boolean | null; // null = "data area, not yet placed"

/** Alignment pattern center positions for Version 4: the position list is
 * [6, 26] (ISO/IEC 18004 Table E.1); all (row,col) combinations EXCEPT
 * (6,6) (which would collide with the top-left finder pattern) get a 5x5
 * alignment pattern -- i.e. exactly the 3 listed here. */
const ALIGNMENT_CENTERS: readonly [number, number][] = [
  [6, 26],
  [26, 6],
  [26, 26],
];

function drawFinderPattern(m: Cell[][], row: number, col: number): void {
  for (let r = -1; r <= 7; r++) {
    if (row + r <= -1 || QR_SIZE <= row + r) continue;
    for (let c = -1; c <= 7; c++) {
      if (col + c <= -1 || QR_SIZE <= col + c) continue;
      const dark = (r >= 0 && r <= 6 && (c === 0 || c === 6)) || (c >= 0 && c <= 6 && (r === 0 || r === 6)) || (r >= 2 && r <= 4 && c >= 2 && c <= 4);
      m[row + r]![col + c] = dark;
    }
  }
}

function drawAlignmentPattern(m: Cell[][], row: number, col: number): void {
  if (m[row]![col] !== null) return; // already claimed by a finder pattern
  for (let r = -2; r <= 2; r++) {
    for (let c = -2; c <= 2; c++) {
      const dark = r === -2 || r === 2 || c === -2 || c === 2 || (r === 0 && c === 0);
      m[row + r]![col + c] = dark;
    }
  }
}

function drawTimingPatterns(m: Cell[][]): void {
  for (let r = 8; r < QR_SIZE - 8; r++) {
    if (m[r]![6] === null) m[r]![6] = r % 2 === 0;
  }
  for (let c = 8; c < QR_SIZE - 8; c++) {
    if (m[6]![c] === null) m[6]![c] = c % 2 === 0;
  }
}

/** The function-pattern-only base grid (finders, separators, timing,
 * alignment) -- identical for every mask/trial, so built once and cloned. */
function buildBaseMatrix(): Cell[][] {
  const m: Cell[][] = Array.from({ length: QR_SIZE }, () => new Array<Cell>(QR_SIZE).fill(null));
  drawFinderPattern(m, 0, 0);
  drawFinderPattern(m, QR_SIZE - 7, 0);
  drawFinderPattern(m, 0, QR_SIZE - 7);
  for (const [row] of ALIGNMENT_CENTERS) {
    for (const [, col] of ALIGNMENT_CENTERS) drawAlignmentPattern(m, row, col);
  }
  drawTimingPatterns(m);
  return m;
}

// ---- format info (BCH(15,5), ISO/IEC 18004 Annex C) --------------------
const G15 = (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | 1; // 0x537
const G15_MASK = (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1); // 0x5412

function bchDigitCount(n: number): number {
  let d = 0;
  let v = n;
  while (v !== 0) {
    d++;
    v >>>= 1;
  }
  return d;
}

/** `data` is the 5-bit (2-bit EC level << 3 | 3-bit mask) value; returns
 * the full 15-bit format-info codeword (5 data bits + 10 BCH bits, XORed
 * with the fixed mask pattern). EC level M's 2-bit indicator is 0b00 (ISO
 * Table 25), so for this M-only encoder `data` is simply the mask 0-7. */
function bchTypeInfo(data: number): number {
  let d = data << 10;
  while (bchDigitCount(d) - bchDigitCount(G15) >= 0) {
    d ^= G15 << (bchDigitCount(d) - bchDigitCount(G15));
  }
  return ((data << 10) | d) ^ G15_MASK;
}

/** Places the 2 redundant copies of the 15-bit format-info string around
 * the finder patterns, plus the single permanently-dark module. When
 * `trial` is true (used only while scoring mask candidates -- see
 * `scoreMask` below) every one of those bits is forced OFF instead,
 * exactly mirroring the reference's `not test` gating -- the trial score
 * must be computed the SAME way for every candidate mask for the
 * comparison to be meaningful, and the reference (which this is ported
 * from, and which this file's golden-fixture test is checked against)
 * scores trials with the format-info area blanked rather than filled with
 * that candidate's real bits. */
function placeFormatInfo(m: Cell[][], mask: number, trial: boolean): void {
  const bits = trial ? 0 : bchTypeInfo(mask);
  for (let i = 0; i < 15; i++) {
    const mod = ((bits >> i) & 1) === 1;
    if (i < 6) m[i]![8] = mod;
    else if (i < 8) m[i + 1]![8] = mod;
    else m[QR_SIZE - 15 + i]![8] = mod;
  }
  for (let i = 0; i < 15; i++) {
    const mod = ((bits >> i) & 1) === 1;
    if (i < 8) m[8]![QR_SIZE - i - 1] = mod;
    else if (i < 9) m[8]![15 - i - 1 + 1] = mod;
    else m[8]![15 - i - 1] = mod;
  }
  m[QR_SIZE - 8]![8] = !trial; // the one permanently-dark module
}

// ---- the 8 mask formulas (ISO/IEC 18004 Table 20) -----------------------
function maskFunc(pattern: number): (r: number, c: number) => boolean {
  switch (pattern) {
    case 0:
      return (r, c) => (r + c) % 2 === 0;
    case 1:
      return (r) => r % 2 === 0;
    case 2:
      return (_r, c) => c % 3 === 0;
    case 3:
      return (r, c) => (r + c) % 3 === 0;
    case 4:
      return (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0;
    case 5:
      return (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0;
    case 6:
      return (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0;
    case 7:
      return (r, c) => (((r * c) % 3) + ((r + c) % 2)) % 2 === 0;
    default:
      throw new Error(`bad mask pattern: ${pattern}`);
  }
}

/** Zigzag data placement (ISO/IEC 18004 §8.7.3): walks two-column strips
 * right-to-left, bottom-to-top then top-to-bottom alternating, skipping the
 * timing-pattern column, filling every still-`null` cell with the next data
 * bit (MSB-first per codeword) XORed with the mask function. Mutates `m` in
 * place and returns it as a fully-resolved `boolean[][]` (every cell must
 * be non-null by the end -- Version 4 has exactly 0 remainder bits per the
 * ISO table, i.e. codewords.length*8 exactly fills the data-capacity
 * region with no leftover `null`s). */
function mapData(m: Cell[][], codewords: readonly number[], mask: number): boolean[][] {
  const maskFn = maskFunc(mask);
  let row = QR_SIZE - 1;
  let inc = -1;
  let byteIndex = 0;
  let bitIndex = 7;

  // NOTE: `colBase` is the loop-stepping variable (32, 30, 28, ..., 2 --
  // matches the reference's `range(modules_count-1, 0, -2)` exactly); `col`
  // is a SEPARATE local that the `col <= 6` adjustment mutates. In the
  // Python original, `col` is rebound fresh from the precomputed `range()`
  // sequence every iteration, so reassigning it inside the loop body has NO
  // effect on the next iteration's value -- a naive JS port that reuses the
  // same variable for both the loop counter AND the in-body adjustment (as
  // an earlier draft of this file did) silently changes the STEPPING
  // sequence too (an off-by-one class of bug that left 32 modules
  // permanently unresolved -- caught by this file's own golden-fixture
  // test, see qr.test.ts).
  for (let colBase = QR_SIZE - 1; colBase > 0; colBase -= 2) {
    let col = colBase;
    if (col <= 6) col -= 1;
    for (;;) {
      for (const c of [col, col - 1]) {
        if (m[row]![c] === null) {
          let dark = false;
          if (byteIndex < codewords.length) {
            dark = ((codewords[byteIndex]! >> bitIndex) & 1) === 1;
          }
          if (maskFn(row, c)) dark = !dark;
          m[row]![c] = dark;
          bitIndex--;
          if (bitIndex === -1) {
            byteIndex++;
            bitIndex = 7;
          }
        }
      }
      row += inc;
      if (row < 0 || row >= QR_SIZE) {
        row -= inc;
        inc = -inc;
        break;
      }
    }
  }

  return m.map((rowArr) =>
    rowArr.map((cell) => {
      if (cell === null) throw new Error('qr: unresolved module after placement (should be unreachable at Version 4)');
      return cell;
    }),
  );
}

// ---- mask penalty scoring (ISO/IEC 18004 §8.8.2, rules 1-4) -------------
// Ported directly from the reference's `_lost_point_level{1..4}` (see this
// file's top comment) -- naive/un-optimized versions of rules 1-3 (the
// reference's "skip via next()" acceleration only skips comparisons that
// would always evaluate false, so the TOTAL score is identical either way,
// just computed less efficiently here, which is fine at 33x33).
function scoreLevel1(m: boolean[][]): number {
  let score = 0;
  const container = new Array<number>(QR_SIZE + 1).fill(0);
  for (let row = 0; row < QR_SIZE; row++) {
    let prev = m[row]![0]!;
    let len = 0;
    for (let col = 0; col < QR_SIZE; col++) {
      if (m[row]![col] === prev) {
        len++;
      } else {
        if (len >= 5) container[len]! += 1;
        len = 1;
        prev = m[row]![col]!;
      }
    }
    if (len >= 5) container[len]! += 1;
  }
  for (let col = 0; col < QR_SIZE; col++) {
    let prev = m[0]![col]!;
    let len = 0;
    for (let row = 0; row < QR_SIZE; row++) {
      if (m[row]![col] === prev) {
        len++;
      } else {
        if (len >= 5) container[len]! += 1;
        len = 1;
        prev = m[row]![col]!;
      }
    }
    if (len >= 5) container[len]! += 1;
  }
  for (let len = 5; len <= QR_SIZE; len++) score += container[len]! * (len - 2);
  return score;
}

function scoreLevel2(m: boolean[][]): number {
  let score = 0;
  for (let row = 0; row < QR_SIZE - 1; row++) {
    for (let col = 0; col < QR_SIZE - 1; col++) {
      const v = m[row]![col]!;
      if (m[row]![col + 1] === v && m[row + 1]![col] === v && m[row + 1]![col + 1] === v) score += 3;
    }
  }
  return score;
}

function scoreLevel3(m: boolean[][]): number {
  let score = 0;
  const short = QR_SIZE - 10;
  for (let row = 0; row < QR_SIZE; row++) {
    for (let col = 0; col < short; col++) {
      const r = m[row]!;
      if (
        !r[col + 1]! &&
        r[col + 4]! &&
        !r[col + 5]! &&
        r[col + 6]! &&
        !r[col + 9]! &&
        ((r[col + 0]! && r[col + 2]! && r[col + 3]! && !r[col + 7]! && !r[col + 8]! && !r[col + 10]!) ||
          (!r[col + 0]! && !r[col + 2]! && !r[col + 3]! && r[col + 7]! && r[col + 8]! && r[col + 10]!))
      ) {
        score += 40;
      }
    }
  }
  for (let col = 0; col < QR_SIZE; col++) {
    for (let row = 0; row < short; row++) {
      const at = (rr: number): boolean => m[rr]![col]!;
      if (
        !at(row + 1) &&
        at(row + 4) &&
        !at(row + 5) &&
        at(row + 6) &&
        !at(row + 9) &&
        ((at(row + 0) && at(row + 2) && at(row + 3) && !at(row + 7) && !at(row + 8) && !at(row + 10)) ||
          (!at(row + 0) && !at(row + 2) && !at(row + 3) && at(row + 7) && at(row + 8) && at(row + 10)))
      ) {
        score += 40;
      }
    }
  }
  return score;
}

function scoreLevel4(m: boolean[][]): number {
  let dark = 0;
  for (const row of m) for (const cell of row) if (cell) dark++;
  const percent = (dark / (QR_SIZE * QR_SIZE)) * 100;
  const rating = Math.floor(Math.abs(percent - 50) / 5);
  return rating * 10;
}

function penaltyScore(m: boolean[][]): number {
  return scoreLevel1(m) + scoreLevel2(m) + scoreLevel3(m) + scoreLevel4(m);
}

/** Builds the full matrix for one candidate mask, WITHOUT the real format
 * info (trial=true, matching the reference's own scoring convention -- see
 * `placeFormatInfo`'s comment), and scores it. */
function scoreMask(base: Cell[][], codewords: readonly number[], mask: number): number {
  const trial = base.map((row) => row.slice());
  placeFormatInfo(trial, mask, true);
  const resolved = mapData(trial, codewords, mask);
  return penaltyScore(resolved);
}

/** Encodes `text` (encoded as UTF-8 bytes) as a Version-4/EC-M QR code and
 * returns the `QR_SIZE`x`QR_SIZE` module matrix (`true` = dark), WITHOUT a
 * quiet zone (the renderer adds that -- ISO/IEC 18004 mandates >= 4 modules
 * of light margin around the symbol). Automatically picks the mask (0-7)
 * that minimizes the standard 4-rule penalty score, same as every
 * spec-compliant encoder. Returns `null` if `text`'s UTF-8 byte length
 * exceeds `QR_MAX_BYTES` -- the caller should fall back to text-only
 * sharing rather than render nothing (see components/ShareRoom.tsx). */
export function encodeQR(text: string): boolean[][] | null {
  const bytes = new TextEncoder().encode(text);
  if (bytes.length > QR_MAX_BYTES) return null;

  const dataCodewords = buildDataCodewords(bytes);
  if (!dataCodewords) return null;
  const codewords = buildInterleavedCodewords(dataCodewords);

  const base = buildBaseMatrix();

  let bestMask = 0;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const score = scoreMask(base, codewords, mask);
    if (score < bestScore) {
      bestScore = score;
      bestMask = mask;
    }
  }

  const final = base.map((row) => row.slice());
  placeFormatInfo(final, bestMask, false);
  return mapData(final, codewords, bestMask);
}
